import { useEffect, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { getErrorMessage } from '@/utils/errorHandlers'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/newRedesign/primitives/Dialog'
import { Button } from '@/components/newRedesign/primitives/Button'
import { Label } from '@/components/newRedesign/primitives/Label'
import { FormSwitch, FormInput } from '@/components/newRedesign/form'
import {
  SelectWithAddButton,
  CreateOriginDestinyDialog,
  type CreateOriginDestinyFormData,
} from '@/components/newRedesign/components'
import { SearchableSelectOption } from '@/components/newRedesign/components/SearchableSelect'
import { createOriginDestiny } from '@/api/originDestinyAPI'
import { useAppSelector } from '@/store/hooks'

interface DefaultItem {
  id: string | number
  name: string
  description?: string
  observations?: string
  code?: string
}

interface EditAdvancedDetailsFormData {
  originId?: number
  destinyId?: number
  netWeight?: number
  grossWeight?: number
  code?: string
  sealNumber?: string
  isRefrigered: boolean
  temperatureRange?: string
  trackingNumber?: string
}

interface EditAdvancedDetailsDialogProps {
  open: boolean
  onOpenChange: (_open: boolean) => void
  onSave: (_data: EditAdvancedDetailsFormData) => void
  onCancel: () => void
  isPending?: boolean
  initialData?: Partial<EditAdvancedDetailsFormData>
  fetchOrigins: (_search: string) => Promise<SearchableSelectOption[]>
  fetchDestinations: (_search: string) => Promise<SearchableSelectOption[]>
  defaultOrigin?: DefaultItem
  defaultDestination?: DefaultItem
}

export function EditAdvancedDetailsDialog({
  open,
  onOpenChange,
  onSave,
  onCancel,
  isPending = false,
  initialData,
  fetchOrigins,
  fetchDestinations,
  defaultOrigin,
  defaultDestination,
}: EditAdvancedDetailsDialogProps) {
  const { control, handleSubmit, reset, setValue } =
    useForm<EditAdvancedDetailsFormData>({
      defaultValues: {
        originId: undefined,
        destinyId: undefined,
        netWeight: undefined,
        grossWeight: undefined,
        code: '',
        sealNumber: '',
        isRefrigered: false,
        temperatureRange: '',
        trackingNumber: '',
      },
    })

  const queryClient = useQueryClient()
  const business = useAppSelector((state) => state.init.business)

  // Estados para controlar los modales de crear
  const [showCreateOriginDialog, setShowCreateOriginDialog] = useState(false)
  const [showCreateDestinationDialog, setShowCreateDestinationDialog] =
    useState(false)

  // Estados para almacenar items recién creados y mostrarlos seleccionados
  const [createdOrigin, setCreatedOrigin] = useState<DefaultItem | null>(null)
  const [createdDestination, setCreatedDestination] =
    useState<DefaultItem | null>(null)

  // Mutación para crear origen
  const createOriginMutation = useMutation({
    mutationFn: (data: CreateOriginDestinyFormData) => {
      // Use countryId provided by the dialog first, otherwise fall back to business address
      const countryId =
        (data as CreateOriginDestinyFormData).countryId ??
        (business?.address as unknown as { countryId?: number })?.countryId
      if (countryId == null) {
        throw new Error('Seleccione un pa\u00eds')
      }
      return createOriginDestiny({
        name: data.name,
        countryId,
      })
    },
    onSuccess: (data) => {
      toast.success('Origen creado correctamente')
      setShowCreateOriginDialog(false)
      // Invalidar cache para refrescar opciones
      queryClient.invalidateQueries({ queryKey: ['originDestiny'] })
      // Establecer el nuevo origen como seleccionado
      if (data?.id && data?.name) {
        setValue('originId', data.id)
        // Guardar el item creado para mostrarlo en el select
        setCreatedOrigin({ id: data.id, name: data.name })
      }
    },
    onError: (err: unknown) => {
      const errorMessage = getErrorMessage(err)
      toast.error(errorMessage || 'Error al crear el origen')
    },
  })

  // Mutación para crear destino
  const createDestinationMutation = useMutation({
    mutationFn: (data: CreateOriginDestinyFormData) => {
      // Use countryId provided by the dialog first, otherwise fall back to business address
      const countryId =
        (data as CreateOriginDestinyFormData).countryId ??
        (business?.address as unknown as { countryId?: number })?.countryId
      if (countryId == null) {
        throw new Error('Seleccione un pa\u00eds')
      }
      return createOriginDestiny({
        name: data.name,
        countryId,
      })
    },
    onSuccess: (data) => {
      toast.success('Destino creado correctamente')
      setShowCreateDestinationDialog(false)
      // Invalidar cache para refrescar opciones
      queryClient.invalidateQueries({ queryKey: ['originDestiny'] })
      // Establecer el nuevo destino como seleccionado
      if (data?.id && data?.name) {
        setValue('destinyId', data.id)
        // Guardar el item creado para mostrarlo en el select
        setCreatedDestination({ id: data.id, name: data.name })
      }
    },
    onError: (err: unknown) => {
      const errorMessage = getErrorMessage(err)
      toast.error(errorMessage || 'Error al crear el destino')
    },
  })

  useEffect(() => {
    if (open && initialData && !isPending) {
      reset({
        originId: initialData.originId,
        destinyId: initialData.destinyId,
        netWeight: initialData.netWeight,
        grossWeight: initialData.grossWeight,
        code: initialData.code || '',
        sealNumber: initialData.sealNumber || '',
        isRefrigered: initialData.isRefrigered || false,
        temperatureRange: initialData.temperatureRange || '',
        trackingNumber: initialData.trackingNumber || '',
      })
    }
  }, [open, initialData, isPending, reset])

  const handleFormSubmit = (data: EditAdvancedDetailsFormData) => {
    if (data.originId && data.destinyId && data.originId === data.destinyId) {
      toast.error('El origen y destino no pueden ser los mismos')
      return
    }

    onSave(data)
  }

  const handleClose = () => {
    if (!isPending) {
      onCancel()
      onOpenChange(false)
    }
  }

  return (
    <Dialog onOpenChange={handleClose} open={open}>
      <DialogContent
        className={
          'flex max-h-[90vh] w-full max-w-[90vw] flex-col overflow-hidden ' +
          'rounded-lg bg-neutral-50 p-0 shadow-lg md:max-h-[800px] md:w-[800px]'
        }
      >
        {/* Header */}
        <DialogHeader className="flex-row items-center justify-between border-b border-neutral-300 p-3 md:p-4">
          <DialogTitle className="text-lg font-semibold tracking-tight text-neutral-800 md:text-xl">
            Detalles avanzados
          </DialogTitle>
        </DialogHeader>

        {/* Content */}
        <form
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
          id="edit-advanced-details-form"
          onSubmit={handleSubmit(handleFormSubmit)}
        >
          <div className="flex-1 space-y-3 overflow-y-auto bg-neutral-50 p-3 md:space-y-4 md:p-4">
            {/* Origen - Destino */}
            <div className="flex flex-col gap-3 md:flex-row md:gap-4">
              {/* Origen */}
              <div className="flex-1">
                <Label className="mb-2 block">Origen</Label>
                <Controller
                  control={control}
                  name="originId"
                  render={({ field }) => (
                    <SelectWithAddButton
                      defaultValue={createdOrigin ?? defaultOrigin}
                      fetcher={fetchOrigins}
                      name="originId"
                      onAdd={() => setShowCreateOriginDialog(true)}
                      onChange={(value) =>
                        field.onChange(value as number | null)
                      }
                      placeholder="Seleccionar origen"
                      searchPlaceholder="Buscar origen..."
                      value={field.value}
                    />
                  )}
                />
              </div>

              {/* Destino */}
              <div className="flex-1">
                <Label className="mb-2 block">Destino</Label>
                <Controller
                  control={control}
                  name="destinyId"
                  render={({ field }) => (
                    <SelectWithAddButton
                      defaultValue={createdDestination ?? defaultDestination}
                      fetcher={fetchDestinations}
                      name="destinyId"
                      onAdd={() => setShowCreateDestinationDialog(true)}
                      onChange={(value) =>
                        field.onChange(value as number | null)
                      }
                      placeholder="Seleccionar destino"
                      searchPlaceholder="Buscar destino..."
                      value={field.value}
                    />
                  )}
                />
              </div>
            </div>

            <div className="flex flex-col gap-3 md:flex-row md:gap-4">
              <div className="flex-1">
                <FormInput
                  control={control}
                  label="Peso neto"
                  name="netWeight"
                  placeholder="0"
                  showLabel
                  type="number"
                />
              </div>
              <div className="flex-1">
                <FormInput
                  control={control}
                  label="Peso bruto"
                  name="grossWeight"
                  placeholder="0"
                  showLabel
                  type="number"
                />
              </div>
            </div>

            {/* Código - Sello */}
            <div className="flex flex-col gap-3 md:flex-row md:gap-4">
              <div className="flex-1">
                <FormInput
                  control={control}
                  label="Código"
                  name="code"
                  placeholder="Código"
                  showLabel
                />
              </div>
              <div className="flex-1">
                <FormInput
                  control={control}
                  label="Sello"
                  name="sealNumber"
                  placeholder="N° de sello"
                  showLabel
                />
              </div>
            </div>

            {/* Refrigerado */}
            <div className="flex items-center gap-2">
              <FormSwitch
                control={control}
                label="Es refrigerado"
                name="isRefrigered"
              />
            </div>

            {/* Temperatura - Tracking */}
            <div className="flex flex-col gap-3 md:flex-row md:gap-4">
              <div className="flex-1">
                <FormInput
                  control={control}
                  label="Temperatura"
                  name="temperatureRange"
                  placeholder="Rango de temperatura"
                  showLabel
                />
              </div>
              <div className="flex-1">
                <FormInput
                  control={control}
                  label="Número de tracking"
                  name="trackingNumber"
                  placeholder="Número de tracking"
                  showLabel
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <DialogFooter className="flex-shrink-0 border-t border-neutral-300 p-3 md:p-4">
            <Button
              className="w-full md:w-auto"
              disabled={isPending}
              form="edit-advanced-details-form"
              type="submit"
              variant="default"
            >
              {isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      {/* Modal para crear origen */}
      <CreateOriginDestinyDialog
        inputLabel="Nombre"
        inputPlaceholder="Ingrese el nombre del origen"
        isPending={createOriginMutation.isPending}
        onCancel={() => setShowCreateOriginDialog(false)}
        onOpenChange={setShowCreateOriginDialog}
        onSave={(data) => createOriginMutation.mutate(data)}
        open={showCreateOriginDialog}
        title="Crear Origen"
      />

      {/* Modal para crear destino */}
      <CreateOriginDestinyDialog
        inputLabel="Nombre"
        inputPlaceholder="Ingrese el nombre del destino"
        isPending={createDestinationMutation.isPending}
        onCancel={() => setShowCreateDestinationDialog(false)}
        onOpenChange={setShowCreateDestinationDialog}
        onSave={(data) => createDestinationMutation.mutate(data)}
        open={showCreateDestinationDialog}
        title="Crear Destino"
      />
    </Dialog>
  )
}
